/**
 * Parsing for server-generated timestamps (created_at / updated_at / sent_at ...).
 *
 * The backend stores naive UTC and several routes still serialize it without an
 * offset ("2026-06-15T03:42:00.967000"). `new Date()` reads such a string as
 * local time, which shifts every timestamp by the viewer's UTC offset (nine
 * hours early in Seoul). This helper labels offset-less date-times as UTC while
 * keeping date-only values ("2026-09-26") on the local calendar day, exactly as
 * `parseDateValuePreservingLocalDay` does for scheduled dates.
 */

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
// "YYYY-MM-DDTHH:MM[:SS[.ffffff]]" with an optional "Z" or "+HH:MM" / "-HHMM" offset.
const DATE_TIME_PATTERN = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2})?)(\.\d+)?(Z|[+-]\d{2}(?::?\d{2})?)?$/i;

export type ServerDateTimeInput = string | number | Date | null | undefined;

// "+0900" and "+09" are valid ISO 8601 but not part of the ECMAScript date format.
const canonicalOffset = (offset: string): string => {
  if (offset.toUpperCase() === 'Z') return 'Z';
  const digits = offset.slice(1).replace(':', '');
  return `${offset[0]}${digits.slice(0, 2)}:${digits.length > 2 ? digits.slice(2, 4) : '00'}`;
};

/**
 * Normalize a server date-time string into a form every engine parses as the
 * intended UTC instant: fractional seconds trimmed to milliseconds and a "Z"
 * appended when the string carries no offset. Non-matching strings return
 * unchanged so `new Date()` keeps its usual behaviour for them.
 */
export const normalizeServerDateTimeString = (value: string): string => {
  const trimmed = value.trim();
  const match = DATE_TIME_PATTERN.exec(trimmed);
  if (!match) {
    return trimmed;
  }
  const [, day, time, fraction, offset] = match;
  const milliseconds = fraction ? `.${fraction.slice(1, 4).padEnd(3, '0')}` : '';
  return `${day}T${time}${milliseconds}${offset ? canonicalOffset(offset) : 'Z'}`;
};

/**
 * Parse a server timestamp. Date-only strings become the local calendar day,
 * offset-less date-times are read as UTC, and everything else (offset strings,
 * epoch numbers, Date instances) parses as `new Date()` would. Empty input
 * yields an invalid Date so callers keep their existing NaN guards.
 */
export const parseServerDateTime = (value: ServerDateTimeInput): Date => {
  if (value instanceof Date) {
    return new Date(value.getTime());
  }
  if (typeof value === 'number') {
    return new Date(value);
  }
  if (typeof value !== 'string' || !value.trim()) {
    return new Date(Number.NaN);
  }
  const dateOnlyMatch = DATE_ONLY_PATTERN.exec(value.trim());
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  return new Date(normalizeServerDateTimeString(value));
};

/** Epoch milliseconds for sorting; NaN-safe (invalid or missing values sort as 0). */
export const serverDateTimeMs = (value: ServerDateTimeInput): number => {
  const time = parseServerDateTime(value).getTime();
  return Number.isFinite(time) ? time : 0;
};
